// @flow

import R from 'ramda';
import * as React from 'react';
import { connect } from 'react-redux';

import DruidQuery from '../DruidQuery';
import DistributionCompareBy from './DistributionCompareBy';
import BaselineCompareBy from './BaselineCompareBy';

import { getDimensionsFromFilterValues } from '../../../support/utils/druid/distribution';

import AppsStore from '../../../../main/stores/AppsStore_LEGACY';
import store from '../../../../main/reducers';
import { setPageState } from '../../../actions/ShareableActions';
import { getDruidFiltersByDimension } from '../../../actions/DruidActions';

import type {
  Filters,
  DruidReducer,
  DruidDateRange
} from '../../../../main/types/druidTypes';
import type { ShareableReducer } from '../../../types/shareableTypes';

type Props = {
  /**
   * type of query to be dispatched (i.e. groupby, topn, etc.)
   */
  type: string,
  /**
   * title displayed at the upper left
   */
  title: string,
  /**
   * query start date
   */
  start_date: string,
  /**
   * query end date
   */
  end_date: string,
  /**
   * array of sources to query. (i.e. eo_install, eo_click, etc.)
   */
  data_sources: Array<string>,
  /**
   * namespace used by shareable reports
   */
  shareableNamespace: string,
  /**
   * context used by druid to track the source of queries
   */
  context: string,
  /**
   * dimensions that the compare by filters roll up into
   */
  compareByDimensions: Array<string>,
  /**
   * Additional queries to be dispatched along with the main query.
   * The result of these queries are meant as baseline to compare against the main query result.
   */
  baselineQueries: Object,
  /**
   * maximum number of compare by filters that can be chosen by the user.
   * Please note, this includes both the main compare by filters as well as the baseline filters
   */
  maxCompareBy: number,
  /**
   * default dimensions for initial query
   */
  defaultDimensions: Array<string>,
  /**
   * default filters for initial query
   */
  defaultFilters: Filters,
  /**
   * compare by filters initially displayed to the user
   */
  defaultCompareByFilters: Array<string>,
  /**
   * time interval initially displayed to the user
   */
  defaultTime: string,
  /**
   * baseline filters initially displayed to the user
   */
  defaultBaselines: Array<string>,
  /**
   * id for testing purposes
   */
  testid: string,
  /**
   * children components to be rendered.
   * NOTE: we pass compare by filters, time, and dimensions to this child
   */
  children: any,
  /**
   * Redux druid object. This component consumes the filter property.
   */
  druid: DruidReducer,
  /**
   * Redux shareable object.
   */
  shareable: ShareableReducer,
  /**
   * method used to update the shareable state
   */
  setPageState: (namespace: string, state: Object) => Object,
  getDruidFiltersByDimension: (
    dateRange: DruidDateRange,
    dimensions: Array<string>
  ) => Function
};

class DistributionQueryBuilder extends React.Component<Props> {
  static defaultProps = {
    maxCompareBy: 6,
    data_sources: ['eo_install'],
    defaultDimensions: [],
    defaultFilters: [],
    defaultCompareByFilters: [],
    defaultTime: 'seconds_from_last_attributed_touch_to_event',
    defaultBaselines: []
  };

  _getShareableState() {
    const { context, shareableNamespace } = this.props;
    return (
      store.getState().shareable.getPageState(shareableNamespace || 'other')[
        context
      ] || {}
    );
  }

  _setShareableState(data) {
    const { context, setPageState, shareableNamespace } = this.props;
    setPageState(shareableNamespace, {
      [context]: R.merge(this._getShareableState(), data)
    });
  }

  componentWillMount() {
    const shareableState = this._getShareableState();
    const props = this.props;

    const initState = {
      query: {
        data_sources: props.data_sources,
        dimensions: props.defaultDimensions.concat(props.defaultTime),
        filters: props.defaultFilters
      },
      compareByFilters: props.defaultCompareByFilters,
      osFilters: props.defaultFilters.user_data_os,
      time: props.defaultTime,
      baselines: props.defaultBaselines
    };

    this._setShareableState(R.merge(initState, shareableState));
  }

  _getQuery = () => {
    const props = this.props;
    const shareableQuery = this._getShareableState().query || {};

    return Object.assign(
      {
        app_id: AppsStore.getAppId(),
        start_date: props.start_date,
        end_date: props.end_date,
        granularity: props.granularity || 'all',
        context: props.context
      },
      shareableQuery
    );
  };

  _getBaselineQueries = () => {
    const shareableBaselines = this._getShareableState().baselines;
    const shareableTime = this._getShareableState().time;
    const shareableQuery = this._getShareableState().query;
    const props = this.props;

    return shareableBaselines.reduce((acc, b) => {
      const baseline = props.baselineQueries[b];
      const dimensions = baseline.dimensions
        ? baseline.dimensions.concat(shareableTime)
        : [shareableTime];
      const filters = R.merge(shareableQuery.filters, baseline.filters);
      const query = Object.assign(
        {
          app_id: AppsStore.getAppId(),
          start_date: props.start_date,
          end_date: props.end_date,
          granularity: props.granularity || 'all',
          context: props.context
        },
        shareableQuery,
        { dimensions },
        { filters }
      );

      return R.merge(acc, { [b]: query });
    }, {});
  };

  _updateCompareByFilters = (filterValues, filters) => {
    const baselineCount = this._getShareableState().baselines.length;
    if (filterValues.length + baselineCount > this.props.maxCompareBy) {
      return;
    }

    const shareableQuery = this._getShareableState().query;
    const dimensions = [
      ...getDimensionsFromFilterValues(filterValues, filters),
      this._getShareableState().time
    ];
    this._setShareableState({
      compareByFilters: filterValues,
      query: R.merge(shareableQuery, { dimensions })
    });
  };

  _updatePlatformFilters = platformFilterValues => {
    const shareableQuery = this._getShareableState().query;
    const filters =
      platformFilterValues.length > 0
        ? {
            user_data_os: platformFilterValues
          }
        : {};

    this._setShareableState({
      osFilters: platformFilterValues,
      query: R.merge(shareableQuery, { filters })
    });
  };

  _updateTime = timeValue => {
    const shareableQuery = this._getShareableState().query;
    const shareableDimensions = shareableQuery.dimensions;

    const dimensions = shareableDimensions.map(dim => {
      if (dim.includes('from_last_attributed_touch_to_event')) {
        return timeValue;
      }
      return dim;
    });

    this._setShareableState({
      time: timeValue,
      query: R.merge(shareableQuery, { dimensions })
    });
  };

  _updateBaselines = baselines => {
    const compareByFilters = this._getShareableState().compareByFilters;
    if (baselines.length + compareByFilters.length > this.props.maxCompareBy) {
      return;
    }
    this._setShareableState({ baselines });
  };

  fetchFilters = dimensions => {
    const { start_date, end_date } = this.props;
    const dateRange = { start_date, end_date };
    return this.props.getDruidFiltersByDimension(dateRange, dimensions);
  };

  render() {
    const { title, druid, compareByDimensions } = this.props;

    const query = this._getQuery();

    const shareableState = this._getShareableState();

    const { osFilters, time, baselines, compareByFilters } = shareableState;

    const baselineQueries =
      osFilters.length > 0 ? this._getBaselineQueries() : {};

    const dimensionsToFilters = R.pick(compareByDimensions, druid.filters);
    const compareByFilterOptions = R.flatten(R.values(dimensionsToFilters));

    return (
      <div className="analytics" data-testid={this.props.testid}>
        {title ? <div className="analytics__title">{title}</div> : null}
        <div className="analytics__container">
          <div className="analytics-filters">
            <DistributionCompareBy
              compareByFilterOptions={compareByFilterOptions}
              updateCompareByFilters={this._updateCompareByFilters}
              updatePlatformFilters={this._updatePlatformFilters}
              updateTime={this._updateTime}
              dimensionsToFilters={dimensionsToFilters}
              compareByFilters={compareByFilters}
              osFilters={osFilters}
              compareByDimensions={compareByDimensions}
              time={time}
              fetchFilters={this.fetchFilters}
            />
            <BaselineCompareBy
              baselines={baselines}
              updateBaselines={this._updateBaselines}
            />
          </div>

          <DruidQuery
            type={this.props.type || 'groupby'}
            query={query}
            unique={false}
            additionalQueries={baselineQueries}
          >
            {React.Children.map(this.props.children, child => {
              if (child) {
                return (
                  React.cloneElement(child, {
                    filters: osFilters.length > 0 ? compareByFilters : [],
                    dimensions: this.props.compareByDimensions,
                    domain: time
                  }) || null
                );
              }
            })}
          </DruidQuery>
        </div>
      </div>
    );
  }
}

const mapDispatchProps = dispatch => ({
  setPageState: (namespace, state) => dispatch(setPageState(namespace, state)),
  getDruidFiltersByDimension: (dateRange, dimensions) =>
    dispatch(getDruidFiltersByDimension(dateRange, dimensions))
});

export default connect(R.pick(['druid', 'shareable']), mapDispatchProps)(
  DistributionQueryBuilder
);
